import { useEffect, useRef, useState } from 'react';
import { TaxAllocationReplaceSchema } from 'shared-models/browser';
import type { TaxAllocationItem, TaxInvoiceView } from 'shared-models/browser';
import { taxAuthorityService, TAX_LIVE_EVENT, TaxLiveEvent } from '../../../../../services/tax-authority/tax-authority.service';
import type { TaxAuthorityProps } from './useTaxAuthorityController';

function applyLive(rows: TaxAllocationItem[], invoice: TaxInvoiceView): TaxAllocationItem[] {
  const same = rows.find((item) => item.id === invoice.id);
  const active = rows.find((item) => item.orderId === invoice.orderId && !item.locallyCancelled);
  if (same?.locallyCancelled || (active && Date.parse(active.updatedAt) > Date.parse(invoice.updatedAt))) return rows;
  if (same && Date.parse(same.updatedAt) === Date.parse(invoice.updatedAt) && same.status !== 'requesting' && invoice.status === 'requesting') return rows;
  const updated = rows.map((item) => item.id === invoice.id ? { ...item, ...invoice }
    : item.orderId === invoice.orderId && !item.locallyCancelled
      ? { ...item, locallyCancelled: true, replacementId: invoice.id } : item);
  return same ? updated : [{ ...invoice, locallyCancelled: false, replacementId: null }, ...updated];
}

export function useTaxAllocationsController({ opened, tenantId, employeeSession }: TaxAuthorityProps) {
  const [items, setItems] = useState<TaxAllocationItem[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [customerVatNumber, setCustomerVatNumber] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const writeLock = useRef(false);
  const readLock = useRef<number | null>(null);
  const live = useRef(new Map<number, TaxInvoiceView>());
  const selected = items.find((item) => item.id === selectedId) ?? null;

  async function load(page: number, scope = generation.current) {
    if (!employeeSession || readLock.current === scope) return;
    readLock.current = scope;
    setLoading(true);
    setError(null);
    try {
      const result = await taxAuthorityService.allocations(tenantId, employeeSession, page);
      if (scope !== generation.current) return;
      setItems((previous) => {
        let rows = page === 1 ? result.items : [...previous, ...result.items.filter((item) => !previous.some((existing) => existing.id === item.id))];
        for (const invoice of live.current.values()) rows = applyLive(rows, invoice);
        return rows;
      });
      setNextPage(result.nextPage);
    } catch (failure) {
      if (scope === generation.current) setError(failure instanceof Error ? failure.message : 'טעינת ההקצאות נכשלה');
    } finally {
      if (readLock.current === scope) readLock.current = null;
      if (scope === generation.current) setLoading(false);
    }
  }

  const loader = useRef(load);
  loader.current = load;
  useEffect(() => {
    const scope = ++generation.current;
    live.current.clear();
    setItems([]);
    setNextPage(null);
    setSelectedId(null);
    setCustomerVatNumber('');
    setConfirmed(false);
    setBusy(false);
    setError(null);
    setLoading(false);
    if (!opened || !employeeSession) return;
    void loader.current(1, scope);
    const onEvent = (raw: Event) => {
      const event = (raw as CustomEvent<TaxLiveEvent>).detail;
      if (event.type !== 'tax.invoice.updated' || event.invoice.tenantId !== tenantId) return;
      const previous = live.current.get(event.invoice.orderId);
      if (previous && Date.parse(previous.updatedAt) > Date.parse(event.invoice.updatedAt)) return;
      live.current.set(event.invoice.orderId, event.invoice);
      setItems((rows) => applyLive(rows, event.invoice));
    };
    window.addEventListener(TAX_LIVE_EVENT, onEvent);
    return () => { generation.current += 1; window.removeEventListener(TAX_LIVE_EVENT, onEvent); };
  }, [opened, tenantId, employeeSession]);

  function select(item: TaxAllocationItem) {
    if (writeLock.current) return;
    setSelectedId(item.id);
    setCustomerVatNumber(item.customerVatNumber === '999999998' ? '' : item.customerVatNumber ?? '');
    setConfirmed(false);
    setError(null);
  }

  const payload = TaxAllocationReplaceSchema.safeParse({ previousInvoiceId: selectedId, customerVatNumber, customerRequestedAllocation: true });
  const canSubmit = !!employeeSession && !!selected && !selected.locallyCancelled && ['approved', 'failed'].includes(selected.status)
    && confirmed && payload.success && (selected.status === 'failed' || customerVatNumber !== selected.customerVatNumber) && !busy && !loading;

  async function replace() {
    if (writeLock.current || !canSubmit || !payload.success || !selected || !employeeSession) return;
    const scope = generation.current;
    writeLock.current = true;
    setBusy(true);
    setError(null);
    try {
      await taxAuthorityService.replace(tenantId, employeeSession, selected.orderId, payload.data);
      if (scope !== generation.current) return;
      setSelectedId(null);
      await load(1, scope);
    } catch (failure) {
      if (scope === generation.current) setError(failure instanceof Error ? failure.message : 'בקשת ההקצאה נכשלה');
    } finally {
      writeLock.current = false;
      if (scope === generation.current) setBusy(false);
    }
  }

  return { items, selected, select, clearSelection: () => { if (!writeLock.current) setSelectedId(null); },
    customerVatNumber, setCustomerVatNumber, confirmed, setConfirmed,
    loading, busy, error, nextPage, canSubmit, replace, refresh: () => load(1), loadMore: () => nextPage && load(nextPage) };
}